import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bar, Countdown, PACE, Pill, StpInfo, VERIFY, WeekGoals, WeekTable, fmtDay, weekLabel } from "./shared";
import StpOptIn, { GoalInputs, parseGoals } from "./StpOptIn";
import StpLeaderboard from "./StpLeaderboard";

function ChangeGoals({ p, nextWeek, onDone }) {
  const cur = p.weeks.find((w) => w.monday >= nextWeek) || p.weeks[p.weeks.length - 1];
  const [goals, setGoals] = useState(Object.fromEntries(Object.entries(cur.goals).map(([k, v]) => [k, String(v)])));
  const save = async () => {
    try { const r = await api.put("/stp/goals", { goals: parseGoals(goals) }); toast.success(`New goals start week of ${fmtDay(r.data.effective_week)}`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="stp-change-goals">
      <p className="text-xs text-slate-600">Applies from the week starting {nextWeek}. Past and current weeks keep their goals.</p>
      <GoalInputs goals={goals} setGoals={setGoals} prefix="stp-edit" />
      <Button onClick={save} disabled={!parseGoals(goals)} data-testid="stp-edit-save" className="rounded-full bg-[#0A1F44] text-white h-9">Save future goals</Button>
    </div>
  );
}

function Progress({ data, readOnly, reload }) {
  const p = data.participant;
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const week = p.weeks.find((w) => w.state === "current");
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-200 p-3 space-y-1.5" data-testid="stp-mg1">
          <div className="flex justify-between items-center"><span className="text-xs font-semibold text-[#0A1F44]">MG1s {p.mg1.count}/20</span>
            <Pill kind={p.mg1.status} map={PACE} testid="stp-mg1-pace" /></div>
          <Bar value={p.mg1.count} max={20} />
          <div className="text-[11px] text-slate-500">{p.mg1.remaining ? `${p.mg1.remaining} to go` : "Target reached"}
            {p.mg1.avg_needed_per_week ? ` · about ${p.mg1.avg_needed_per_week} a week over ${p.mg1.weeks_left} week(s) left` : ""}. Pace, not a prediction.</div>
        </div>
        <div className="rounded-lg border border-slate-200 p-3 space-y-1.5" data-testid="stp-launch">
          <div className="flex justify-between items-center"><span className="text-xs font-semibold text-[#0A1F44]">Launch {p.launch.count}/1</span>
            {p.launch.achieved && <span className="text-[11px] font-semibold text-emerald-700">Achieved</span>}</div>
          <Bar value={p.launch.count} max={1} />
          <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">Reward: {p.reward.qualified ? "threshold reached" : "not yet"}
            <Pill kind={p.reward.verification} map={VERIFY} testid="stp-verify-status" /></div>
        </div>
      </div>
      {week && <div className="space-y-1.5"><div className="text-xs text-slate-500">{weekLabel(week)} — weekly goals</div>
        <WeekGoals w={week} testid="stp-this-week" /></div>}
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <Button variant="outline" onClick={() => setOpen((o) => !o)} data-testid="stp-toggle-weeks" className="rounded-full h-8">{open ? "Hide" : "Show"} weekly breakdown</Button>
        {!readOnly && data.open && <Button variant="outline" onClick={() => setEdit((o) => !o)} data-testid="stp-edit-open" className="rounded-full h-8">Change future goals</Button>}
      </div>
      {edit && <ChangeGoals p={p} nextWeek={data.next_goal_week} onDone={() => { setEdit(false); reload(); }} />}
      {open && <>
        <WeekTable weeks={p.weeks} />
        <div className="text-[11px] text-slate-500" data-testid="stp-totals">Run to date: {p.totals.conversations} conversations · {p.totals.contact_exchanges} CEs · {p.totals.dtms} DTMs · {p.totals.mg1s} MG1s · {p.totals.launches} launches</div>
      </>}
    </div>
  );
}

export default function StpCard() {
  const { user, isViewAs } = useAuth();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const load = useCallback(() => api.get("/stp/me").then((r) => {
    setData(r.data);
    if (!r.data.participant) { setBoard(null); return; }
    api.get("/stp/leaderboard").then((b) => setBoard(b.data.entries)).catch(() => setBoard(null));
  }).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!data || (!data.participant && !data.open)) return null;
  const readOnly = isViewAs || user?.role === "oversight";
  return (
    <Card className="p-4 sm:p-5 space-y-3 border-[#C9A961]/50" data-testid="stp-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0A1F44] text-[#C9A961] flex items-center justify-center shrink-0"><Flag className="w-5 h-5" aria-hidden="true" /></div>
          <div>
            <h2 className="font-serif text-xl text-[#0A1F44] leading-tight" data-testid="stp-title">STP Run</h2>
            <p className="text-sm text-slate-600" data-testid="stp-subtitle">Optional work ethic challenge</p>
            <p className="text-xs font-semibold tracking-wide text-[#8A6D1F] mt-0.5" data-testid="stp-dates">1 Nov - 10 Feb 2027</p>
          </div>
        </div>
        {data.participant && <Countdown />}
      </div>
      <StpInfo />
      {data.participant ? <><Progress data={data} readOnly={readOnly} reload={load} /><StpLeaderboard entries={board} /></>
        : readOnly ? <p className="text-xs text-slate-500">Not taking part.</p> : <StpOptIn onDone={load} />}
    </Card>
  );
}
